import { format } from 'date-fns';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import ReportActions from '../ReportActions';
import { ReportType, ReportOption } from './reportConfig';
import { ReportFilters } from '../../services/reports';

interface ReportHeaderProps {
  selectedReport: ReportType;
  selectedReportOption?: ReportOption;
  dateRange: string;
  onDateRangeChange: (value: string, newFilters: ReportFilters) => void;
  filters: ReportFilters;
  onFiltersChange: (filters: ReportFilters) => void;
  onBack: () => void;
  getDateFilters: (dateRange: string) => ReportFilters;
  shareUrl?: string;
  onExportPDF?: () => void | Promise<void>;
  onEmailShare?: () => void | Promise<void>;
  onExportCSV?: () => void;
  reportData?: unknown;
}

export default function ReportHeader({
  selectedReport,
  selectedReportOption,
  dateRange,
  onDateRangeChange,
  filters,
  onFiltersChange,
  onBack,
  getDateFilters,
  shareUrl,
  onExportPDF,
  onEmailShare,
  onExportCSV,
  reportData,
}: ReportHeaderProps) {
  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-600 print:hidden"
            >
              <ChevronLeftIcon className="h-4 w-4" aria-hidden />
              Back to Reports
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mt-2">
            {selectedReportOption?.name}
          </h1>
          <p className="mt-2 text-sm text-slate-700">
            {selectedReportOption?.description}
          </p>
          <p className="text-xs text-slate-500 mt-1 print:block">
            Generated on {format(new Date(), 'PPP')} • Period: {dateRange.replace(/(\d+)/, '$1 ')}
          </p>
        </div>

        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none print:hidden">
          <div className="flex items-center space-x-4">
            {selectedReport === 'birds-listing' && (
              <div className="flex items-center space-x-2">
                <select
                  value={filters.status || ''}
                  onChange={(e) => {
                    const newFilters = { ...filters };
                    if (e.target.value) newFilters.status = e.target.value;
                    else delete newFilters.status;
                    onFiltersChange(newFilters);
                  }}
                  className="block rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-cyan-500 focus:ring-cyan-500"
                >
                  <option value="">All Status</option>
                  <option value="BREEDING">Breeding</option>
                  <option value="AVAILABLE">Available</option>
                  <option value="SOLD">Sold</option>
                  <option value="PERSONAL">Personal</option>
                </select>
                <label className="flex items-center text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={filters.includeInactive || false}
                    onChange={(e) => {
                      const newFilters = { ...filters };
                      newFilters.includeInactive = e.target.checked;
                      if (!e.target.checked) delete newFilters.includeInactive;
                      onFiltersChange(newFilters);
                    }}
                    className="mr-2 h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  />
                  Include inactive
                </label>
              </div>
            )}

            {['breeding-list', 'income-statement'].includes(selectedReport) && (
              <select
                value={dateRange}
                onChange={(e) => {
                  const value = e.target.value;
                  onDateRangeChange(value, getDateFilters(value));
                }}
                className="block rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-cyan-500 focus:ring-cyan-500"
              >
                <option value="all">All Data</option>
                <option value="7days">Last 7 days</option>
                <option value="30days">Last 30 days</option>
                <option value="90days">Last 90 days</option>
                <option value="12months">Last 12 months</option>
                <option value="year">Last year</option>
              </select>
            )}

            <ReportActions
              reportTitle={selectedReportOption?.name || 'Report'}
              shareUrl={shareUrl}
              onExportPDF={onExportPDF}
              onEmailShare={onEmailShare}
              onExportCSV={onExportCSV}
              reportData={reportData}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
